import { useCallback, useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/api";

export function useStudentGame() {
  const [packs, setPacks] = useState(null);
  const [selectedPack, setSelectedPack] = useState("");
  const [gameType, setGameType] = useState("rapid_fire");
  const [run, setRun] = useState(null);
  const [items, setItems] = useState([]);
  const [index, setIndex] = useState(0);
  const [answer, setAnswer] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [seconds, setSeconds] = useState(0);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/student/home")
      .then(({ data }) => {
        const ready = data.study_packs.filter((pack) => pack.status === "ready" && pack.questions > 0);
        setPacks(ready);
        if (ready.length) setSelectedPack(ready[0].id);
      })
      .catch((err) => setError(apiError(err, "No pudimos cargar tus juegos.")));
  }, []);

  const finish = useCallback(async () => {
    if (!run || run.status !== "active") return;
    try {
      const { data } = await api.post(`/games/${run.id}/finish`);
      setRun(data.run);
    } catch (err) {
      setError(apiError(err, "No pudimos cerrar la partida."));
    }
  }, [run]);

  useEffect(() => {
    if (!run || run.status !== "active") return undefined;
    const update = () => {
      const remaining = Math.max(0, Math.ceil((new Date(run.expires_at).getTime() - Date.now()) / 1000));
      setSeconds(remaining);
      if (remaining === 0) finish();
    };
    update();
    const timer = window.setInterval(update, 250);
    return () => window.clearInterval(timer);
  }, [run, finish]);

  const start = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/games", { study_pack_id: selectedPack, game_type: gameType });
      setRun(data.run);
      setItems(data.items);
      setIndex(0);
      setFeedback(null);
      setAnswer(initialAnswer(data.items[0]));
    } catch (err) {
      setError(apiError(err, "No hay ejercicios compatibles con este juego."));
    } finally {
      setBusy(false);
    }
  };

  const current = items[index];
  const canSubmit = useMemo(() => validAnswer(current, answer), [current, answer]);

  const submit = async () => {
    if (!canSubmit || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/games/${run.id}/answers`, {
        question_id: current.id,
        answer,
      });
      setFeedback(data);
      setRun(data.run);
    } catch (err) {
      setError(apiError(err, "No pudimos guardar la respuesta."));
    } finally {
      setBusy(false);
    }
  };

  const next = () => {
    if (index + 1 >= items.length || run.status !== "active") return;
    const nextIndex = index + 1;
    setIndex(nextIndex);
    setFeedback(null);
    setAnswer(initialAnswer(items[nextIndex]));
  };

  const reset = () => {
    setRun(null);
    setItems([]);
  };

  return {
    packs,
    selectedPack,
    setSelectedPack,
    gameType,
    setGameType,
    run,
    items,
    index,
    answer,
    setAnswer,
    feedback,
    seconds,
    error,
    busy,
    current,
    canSubmit,
    start,
    submit,
    next,
    reset,
  };
}

export function initialAnswer(question) {
  if (!question) return null;
  if (question.type === "matching") return { pairs: [] };
  if (question.type === "ordering") return { items: [...(question.payload.items || [])] };
  if (question.type === "fill_blank") return { text: "" };
  return null;
}

export function validAnswer(question, answer) {
  if (!question || !answer) return false;
  if (question.type === "multiple_choice") return Number.isInteger(answer.selected_index);
  if (question.type === "true_false") return typeof answer.value === "boolean";
  if (question.type === "fill_blank") return Boolean(answer.text?.trim());
  if (question.type === "ordering") return answer.items?.length === question.payload.items?.length;
  if (question.type === "matching") return answer.pairs?.length === question.payload.lefts?.length;
  return false;
}
